import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { tokenStore } from '../lib/auth-token';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // Skip server roundtrip if no token in localStorage AND no cookie likely
    // (browsers don't expose http-only cookies to JS, so we always try once).
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      return response.data;
    } catch (error) {
      setUser(null);
      // Token invalid or missing — clear any stale entries
      if (error?.response?.status === 401) {
        tokenStore.clear();
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(
      `${API}/auth/login`,
      { email, password }
    );
    const data = response.data || {};
    if (data.access_token) {
      tokenStore.set(data.access_token, data.refresh_token);
    }
    if (data.user) {
      setUser(data.user);
      setLoading(false);
    } else {
      await checkAuth();
    }
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } finally {
      tokenStore.clear();
      setUser(null);
    }
  };

  const setPassword = async (email, password, confirmPassword, resultId) => {
    const response = await axios.post(
      `${API}/auth/set-password`,
      {
        email,
        password,
        confirm_password: confirmPassword,
        result_id: resultId
      }
    );
    const data = response.data || {};
    if (data.access_token) {
      tokenStore.set(data.access_token, data.refresh_token);
    }
    if (data.user) {
      setUser(data.user);
      setLoading(false);
    } else {
      await checkAuth();
    }
    return data;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    setPassword,
    checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
